// @ts-strict-ignore
import { ISpanData } from '../span-data';
import { ConstraintCheckResults } from './constraint-check-results';
import { ConstraintCheckResult } from './constraint-check-result';
import { ResultCode } from './result-code';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { LabelEditorUtils } from '../label-editor-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { InitManager } from '@shared/manager/init.manager';
import { TextSizeCheckUtils } from '@shared/util/text-size-check-utils';
import { TextSizeCheckVO } from '@3d-scene/manager/text-size-check.vo';
import { CellLabelSizeInfoUtils } from '@shared/util/cell-label-size-info-utils';
import { CompressionSimpleTextValueVO, SimpleTextValueVO } from '@shared/vo/meta-element.vo';
import { Dictionary } from 'typescript-collections';
import Quill from 'quill';
import { HtmlUtils } from '@shared/util/html-utils';

export class ConstraintCheckUtils {
  private static checkForSpanDataConstraintViolations(
    useStrictConformityChecking: boolean,
    localeResourceManager: LocaleResourceManager,
    spanData: ISpanData,
    isBold: boolean,
    textRotation: number,
    usedCompression: number,
    usedFontSize: number,
    autoFormatByTextCheck: boolean,
    textValue: string = null,
    html5data: string = null
  ): ConstraintCheckResults {
    const results: ConstraintCheckResults = new ConstraintCheckResults();
    if (!spanData.textCheckClass) {
      return results;
    }
    let text = this.getText(textValue, html5data, spanData);
    const originalText: string = text;
    // remove all special chars used for formatting purposes (\uFEFF = &#65279;)
    text = text.replaceAll('\uFEFF', '');
    const lines: string[] = text.split('\n');
    const currentUsedLines: number = lines.length;

    // keep track of first and last characters in each line
    const startAndEndPositions: Dictionary<number, number[]> = ConstraintCheckUtils.gatherStartEndPositionsPerLine(originalText.split('\n'));

    // font, fontsize and boldness of font is ignored here
    const isRotated: number = textRotation === 90 || textRotation === 270 ? 1 : 0;
    const currentMaxCharInLines: number = Math.max(0, ...lines.map((line) => line.length));

    // NGI-362
    const multiLineCount: number = spanData.currentMultilineCount;
    const fontBold = isBold ? 1 : 0;
    const maxLines: number = spanData.getMaxPermittedLineInLineCount(null, Number.NaN, fontBold, isRotated, Number.NaN, multiLineCount);
    const warningChars: number = spanData.getMaxPermittedCharCountPerLine(null, Number.NaN, fontBold, isRotated, currentUsedLines, multiLineCount);
    const maxChars: number = spanData.getMaxPermittedCharCountPerLine(null, Number.NaN, fontBold, isRotated, Number.NaN, multiLineCount);
    const rotationAdjustedMaxCharInLines: number =
      isRotated === 0
        ? NumberUtils.roundValueByRoundOffLimit(currentMaxCharInLines / spanData.colSpan, InitManager.spacingDecidingValue, 1)
        : currentMaxCharInLines;
    const rotationAdjustedUsedLines: number =
      isRotated === 0
        ? currentUsedLines
        : NumberUtils.roundValueByRoundOffLimit(currentUsedLines / spanData.colSpan, InitManager.spacingDecidingValue, 1);

    const fontSize: number = spanData.getCurrentFontSize(
      null,
      fontBold,
      isRotated,
      rotationAdjustedMaxCharInLines,
      rotationAdjustedUsedLines,
      multiLineCount
    );
    results.fontSize = fontSize;

    if ([maxLines, maxChars].every((value) => !Number.isNaN(value))) {
      let colspannedMaxChars: number;
      let colspannedWarningChars: number;
      let colspannedMaxLines: number;
      if (isRotated === 0) {
        colspannedMaxChars = spanData.colSpan * maxChars;
        colspannedWarningChars = spanData.colSpan * warningChars;
        colspannedMaxLines = maxLines;
      } else {
        colspannedMaxChars = maxChars;
        colspannedWarningChars = warningChars;
        colspannedMaxLines = spanData.colSpan * maxLines;
      }
      // Check for max number of lines
      results.addAll(ConstraintCheckUtils.checkForNumberOfLinesViolation(localeResourceManager, lines, startAndEndPositions, colspannedMaxLines));
      // Check for max number of chars per line
      results.addAll(
        ConstraintCheckUtils.checkForNumberOfCharsPerLineViolation(
          localeResourceManager,
          lines,
          startAndEndPositions,
          colspannedMaxChars,
          colspannedWarningChars
        )
      );
    }
    if (useStrictConformityChecking && results.length < 1) {
      if (
        !ConstraintCheckUtils.validateStrictTextCheckConformityForSpanData(
          spanData,
          isBold,
          textRotation,
          usedCompression,
          usedFontSize,
          autoFormatByTextCheck,
          textValue,
          html5data
        )
      ) {
        const configResult: ConstraintCheckResult = new ConstraintCheckResult(ConstraintCheckResult.FAILED);
        configResult.checkDetails = localeResourceManager.getValueByKey('validation.error.non_default_textcheck');
        configResult.resultCode = ResultCode.NO_STRICT_VALID_CONFIG_FOUND;
        results.addItem(configResult);
        results.containsStrictConformityErrors = true;
      }
    }
    // If no violations were found, the checks passed!
    if (results.length < 1) {
      results.addItem(new ConstraintCheckResult(ConstraintCheckResult.PASSED));
    }
    // NGI-362: Scale factor must only be applied to non-rotated cells:
    const useScaleFactor: boolean = spanData.colSpan > 1 && isRotated === 0;
    results.textCompression = TextSizeCheckUtils.getCompressionByParameters(
      spanData.cellLabelSizeInfos,
      spanData.textCheckClass,
      fontBold,
      rotationAdjustedMaxCharInLines,
      rotationAdjustedUsedLines,
      isRotated,
      multiLineCount,
      NaN,
      useScaleFactor
    );
    return results;
  }

  private static getText(textValue: string, html5data: string, spanData: ISpanData) {
    let t: string = textValue ?? LabelEditorUtils.extractTextFromHTML5Data(html5data ?? spanData.html5data) ?? '';
    // removing last new line charachter at end of text, as it was introduced (by quill or custom conversion process)
    // for last paragraph element in html5 text
    const lastNewLineIndex: number = t.lastIndexOf('\n');
    if (lastNewLineIndex >= 0) {
      t = t.substring(0, lastNewLineIndex);
    }
    return t;
  }

  private static gatherStartEndPositionsPerLine(lines: string[]): Dictionary<number, number[]> {
    const hm: Dictionary<number, number[]> = new Dictionary<number, number[]>();
    let firstPos: number = 0;
    for (let i: number = 0; i < lines.length; i++) {
      const line: string = lines[i];
      let formattingCharCountAtBeginning: number = 0;
      formatCharLoop: for (let j: number = 0; j < line.length; j++) {
        if (line.charAt(j) === '\uFEFF') {
          formattingCharCountAtBeginning++;
        } else {
          break formatCharLoop;
        }
      }
      const lastPos: number = firstPos + line.length - 1;
      if (line.length > 0) {
        hm.setValue(i, [firstPos, lastPos, formattingCharCountAtBeginning]);
      }
      firstPos = lastPos + 2; // +1 to next new char and +1 for newline charachter (/n)
    }
    return hm;
  }

  /**
   * Check for max number of chars per line
   *
   * @param lines current lines in textfield
   * @param startAndEndPositions array defining beginning and ending index of each line
   */
  private static checkForNumberOfCharsPerLineViolation(
    localeResourceManager: LocaleResourceManager,
    lines: string[],
    startAndEndPositions: Dictionary<number, number[]>,
    maxCharsPerLine: number,
    warningCharsPerLine: number = NaN
  ): ConstraintCheckResults {
    const results: ConstraintCheckResults = new ConstraintCheckResults();
    if (!isNaN(maxCharsPerLine)) {
      for (let j: number = 0; j < lines.length; j++) {
        // Add warning to each line
        if (!isNaN(warningCharsPerLine) && warningCharsPerLine !== maxCharsPerLine && lines[j].length > warningCharsPerLine) {
          const ar: number[] = startAndEndPositions.getValue(j);
          if (ar) {
            const lengthWarningResult: ConstraintCheckResult = new ConstraintCheckResult(ConstraintCheckResult.FAILED);
            lengthWarningResult.checkDetails = localeResourceManager.getValueByKey('validation.error.too_many_chars_for_linecount', [
              warningCharsPerLine,
            ]);
            lengthWarningResult.resultCode = ResultCode.TOO_MANY_CHARACTERS_FOR_LINECOUNT;
            lengthWarningResult.errorStart = ar[0] + ar[2] + warningCharsPerLine;
            const a: number = ar[0] + ar[2] + maxCharsPerLine - 1;
            const b: number = ar[1];
            lengthWarningResult.errorEnd = Math.min(a, b);
            results.addItem(lengthWarningResult);
          }
        }
        // Add error to each line
        if (lines[j].length > maxCharsPerLine) {
          const arr: number[] = startAndEndPositions.getValue(j);
          if (arr) {
            const lengthFailedResult: ConstraintCheckResult = new ConstraintCheckResult(ConstraintCheckResult.FAILED);
            lengthFailedResult.checkDetails = localeResourceManager.getValueByKey('validation.error.too_many_chars', [maxCharsPerLine]);
            lengthFailedResult.resultCode = ResultCode.TOO_MANY_CHARACTERS_IN_LINE;
            lengthFailedResult.errorStart = arr[0] + arr[2] + maxCharsPerLine;
            lengthFailedResult.errorEnd = arr[1];
            results.addItem(lengthFailedResult);
          }
        }
      }
    }
    return results;
  }

  /**
   * Check for max number of lines
   */
  private static checkForNumberOfLinesViolation(
    localeResourceManager: LocaleResourceManager,
    lines: string[],
    startAndEndPositions: Dictionary<number, number[]>,
    maxLines: number
  ): ConstraintCheckResults {
    const results: ConstraintCheckResults = new ConstraintCheckResults();
    // Add failure marker
    for (let k: number = maxLines; k < lines.length; k++) {
      const a: number[] = startAndEndPositions.getValue(k);
      // only check non empty lines
      if (a) {
        const linecountResult: ConstraintCheckResult = new ConstraintCheckResult(ConstraintCheckResult.FAILED);
        linecountResult.checkDetails = localeResourceManager.getValueByKey('validation.error.too_many_lines', [maxLines]);
        linecountResult.resultCode = ResultCode.TOO_MANY_LINES;
        linecountResult.errorStart = a[0];
        linecountResult.errorEnd = a[1];
        results.addItem(linecountResult);
      }
    }
    return results;
  }

  // NGI-362
  private static validateStrictTextCheckConformityForSpanData(
    spanData: ISpanData,
    isBold: boolean = null,
    textRotation: number = NaN,
    usedCompression: number = NaN,
    usedFontSize: number = NaN,
    autoFormatByTextCheck: boolean = null,
    textValue: string = null,
    html5data: string = null
  ): boolean {
    if (spanData === null) {
      return true;
    }
    if (isNaN(textRotation)) {
      textRotation = spanData.labelFormat.direction;
    }
    const isRotated: number = textRotation === 90 || textRotation === 270 ? 1 : 0;
    if (isBold === null) {
      if (html5data !== null) {
        isBold = html5data.indexOf('<strong') >= 0;
      } else {
        isBold = spanData.html5data.indexOf('<strong') >= 0;
      }
    }
    const multiLineCount: number = spanData.currentMultilineCount;
    const cellLabelSizeInfos: TextSizeCheckVO[] = CellLabelSizeInfoUtils.getMultiLineAdjustedCellLabelSizeInfos(
      spanData.cellLabelSizeInfos,
      multiLineCount,
      isRotated
    );
    if (cellLabelSizeInfos == null || cellLabelSizeInfos.length === 0) {
      return false;
    }
    let text: string = this.getText(textValue, html5data, spanData);
    // remove all special chars used for formatting purposes (\uFEFF = &#65279;)
    text = text.replaceAll('\uFEFF', '');
    const lines: string[] = text.split('\n');
    const lineCount: number = lines.length;
    let currentMaxCharInLines: number = 0;
    for (let m: number = 0; m < lines.length; m++) {
      if (currentMaxCharInLines < lines[m].length) {
        currentMaxCharInLines = lines[m].length;
      }
    }
    const colspannedChars: number =
      isRotated === 0
        ? NumberUtils.roundValueByRoundOffLimit(currentMaxCharInLines / spanData.colSpan, InitManager.spacingDecidingValue, 1)
        : currentMaxCharInLines;
    const colspannedLines: number =
      isRotated === 0 ? lineCount : NumberUtils.roundValueByRoundOffLimit(lineCount / spanData.colSpan, InitManager.spacingDecidingValue, 1);
    const compression: number = isNaN(usedCompression) || usedCompression === -1 ? spanData.compression : usedCompression;
    const isBoldNumber: number = isBold ? 1 : 0;
    if (autoFormatByTextCheck === null ? spanData.autoFormatByTextCheck : autoFormatByTextCheck) {
      for (const clsi of cellLabelSizeInfos) {
        if (
          clsi !== null &&
          isBoldNumber === clsi.fontBold &&
          isRotated === clsi.rotation &&
          colspannedChars <= clsi.chars &&
          colspannedLines <= clsi.lines
        ) {
          return true;
        }
      }
    } else {
      const fontSize: number = isNaN(usedFontSize)
        ? LabelEditorUtils.getFontSizeFromHTML5Data(html5data === null ? spanData.html5data : html5data)
        : usedFontSize;
      if (isNaN(fontSize) || fontSize <= 0) {
        return false;
      }
      for (const clsi of cellLabelSizeInfos) {
        if (
          clsi !== null &&
          /*fontName == clsi.fontName &&*/
          fontSize === clsi.fontSize &&
          isBoldNumber === clsi.fontBold &&
          isRotated === clsi.rotation &&
          colspannedChars <= clsi.chars &&
          colspannedLines <= clsi.lines &&
          compression === clsi.compression
        ) {
          return true;
        }
      }
    }
    return false;
  }

  public static performValidationOnSpanData(
    useStrictConformityChecking: boolean,
    localeResourceManager: LocaleResourceManager,
    spanData: ISpanData,
    textRotation: number = -1,
    compression: number = NaN,
    autoFormatByTextCheck: boolean = null,
    html5data: string = null
  ): ConstraintCheckResults {
    let isBold: boolean;
    if (html5data !== null) {
      isBold = html5data.indexOf('<strong') >= 0;
    } else {
      isBold = spanData.html5data.indexOf('<strong') >= 0;
    }
    if (textRotation === -1 || isNaN(textRotation)) {
      textRotation = spanData.labelFormat.direction;
    }
    const constraintCheckResults: ConstraintCheckResults = ConstraintCheckUtils.checkForSpanDataConstraintViolations(
      useStrictConformityChecking,
      localeResourceManager,
      spanData,
      isBold,
      textRotation,
      isNaN(compression) || compression === -1 ? spanData.compression : compression,
      NaN,
      autoFormatByTextCheck === null ? spanData.autoFormatByTextCheck : autoFormatByTextCheck,
      null,
      html5data
    );
    return ConstraintCheckUtils.updateErrorHighlightsInEditorAccordingToConstraintCheckResults(null, constraintCheckResults, true);
  }

  public static performValidationOnCurrentTextInEditor(
    useStrictConformityChecking: boolean,
    localeResourceManager: LocaleResourceManager,
    editor: Quill,
    spanData: ISpanData
  ): ConstraintCheckResults {
    // quill editor does not provide correct font information when default font size is used together with another one.
    // its a minor problem, so we dont do anything to fix this problem from quill
    const format = editor.getFormat(0, editor.getLength());
    const isBold: boolean = !!format['bold'];
    let usedFontSize: number;
    if (Array.isArray(format['size'])) {
      usedFontSize = -1; // -1 means that the default font size is used
    } else {
      if (format['size'] === undefined || format['size'] === null || typeof format['size'] !== 'string') {
        usedFontSize = LabelEditorUtils.DEFAULT_FONT_SIZE;
      } else {
        usedFontSize =  parseInt(format['size'], 10);
      }
    }
    usedFontSize = LabelEditorUtils.convertHTML5DataFontSizeToFontSize(usedFontSize);
    const constraintCheckResults: ConstraintCheckResults = ConstraintCheckUtils.checkForSpanDataConstraintViolations(
      useStrictConformityChecking,
      localeResourceManager,
      spanData,
      isBold,
      spanData.labelFormat.direction,
      spanData.compression,
      usedFontSize,
      spanData.autoFormatByTextCheck,
      editor.getText()
    );
    return ConstraintCheckUtils.updateErrorHighlightsInEditorAccordingToConstraintCheckResults(editor, constraintCheckResults, false);
  }

  public static clearErrorHighlightsInEditor(editor: Quill, removeNoLongerNeededFormattingChars: boolean): void {
    if (editor === null) {
      return;
    }
    const currentText: string = editor.getText();
    editor.formatText(0, currentText.length, { background: '' }, 'api');
    if (removeNoLongerNeededFormattingChars && currentText && currentText.length > 1) {
      let previousChar: string = currentText.charAt(currentText.length - 1);
      for (let i: number = currentText.length - 2; i >= 0; i--) {
        if (currentText.charAt(i) === '\uFEFF') {
          if (previousChar !== '\n' || (i - 1 >= 0 && currentText.charAt(i - 1) !== '\n')) {
            editor.deleteText(i, 1, 'api');
          } else {
            previousChar = currentText.charAt(i);
          }
        }
      }
    }
  }

  private static updateErrorHighlightsInEditorAccordingToConstraintCheckResults(
    editor: Quill,
    constraintCheckResults: ConstraintCheckResults,
    onlyCheckIfResultContainsErrors: boolean
  ): ConstraintCheckResults {
    const currentText = onlyCheckIfResultContainsErrors ? null : editor.getText();
    let containsErrors: boolean = false;
    if (!onlyCheckIfResultContainsErrors && editor !== null) {
      // clear all existing error markings
      editor.formatText(0, currentText.length, { background: '' }, 'api');
    }
    // add new error markings
    let errorTooltipText: string = '';
    const errorDetailsUsedInTooltipText: Set<string> = new Set<string>();
    if (constraintCheckResults && constraintCheckResults.length > 0) {
      for (const result of constraintCheckResults.getAll()) {
        if (result.checkResult === ConstraintCheckResult.FAILED) {
          if (result.errorStart !== -1 && result.errorEnd !== -1) {
            if (!onlyCheckIfResultContainsErrors && editor !== null) {
              // highlight text
              const isWarning: boolean =
                result.resultCode === ResultCode.TOO_MANY_CHARACTERS_FOR_LINECOUNT || result.resultCode === ResultCode.TOO_MANY_LINES_FOR_CHARCOUNT;
              const backgroundColor: string = isWarning ? '#FFB01C' : '#FF0000';
              editor.formatText(result.errorStart, result.errorEnd - result.errorStart + 1, { background: backgroundColor }, 'api');
            }
            if (result.checkDetails && !errorDetailsUsedInTooltipText.has(result.checkDetails)) {
              errorTooltipText += containsErrors ? '<br>' + result.checkDetails : result.checkDetails;
              errorDetailsUsedInTooltipText.add(result.checkDetails);
            }
            if (result.resultCode !== ResultCode.NO_STRICT_VALID_CONFIG_FOUND) {
              containsErrors = true; // warnings are also considered to be errors in this regard
            }
          } else {
            if (result.checkDetails && !errorDetailsUsedInTooltipText.has(result.checkDetails)) {
              errorTooltipText += containsErrors ? '<br>' + result.checkDetails : result.checkDetails;
              errorDetailsUsedInTooltipText.add(result.checkDetails);
            }
            if (result.resultCode !== ResultCode.NO_STRICT_VALID_CONFIG_FOUND) {
              containsErrors = true; // highlight field (always marked as error)
            }
          }
        }
      }
    }
    if (!onlyCheckIfResultContainsErrors && editor !== null) {
      if (errorTooltipText) {
        editor['container'].dataset.errorTooltipText = errorTooltipText;
      } else {
        delete editor['container'].dataset.errorTooltipText;
      }
    }
    constraintCheckResults.containsErrors = containsErrors;
    constraintCheckResults.errorTooltipText = errorTooltipText;
    return constraintCheckResults;
  }

  public static calculateCompressionForTextValue(
    textValue: SimpleTextValueVO | CompressionSimpleTextValueVO,
    textSizeCheckInfos: TextSizeCheckVO[],
    textCheckClass: string,
  ): number {
    if (!textValue) {
      return TextSizeCheckUtils.DEFAULT_COMPRESSION;
    }

    const { fontBold, rotation, chars, lines } = ConstraintCheckUtils.getTextCheckParametersFromTextValue(textValue);

    return TextSizeCheckUtils.getCompressionByParameters(
      textSizeCheckInfos,
      textCheckClass,
      fontBold,
      chars,
      lines,
      rotation,
      1,
      Math.max(...HtmlUtils.extractLegacyFontSizeValues(textValue.text)),
      false,
    );
  }

  /**
   * Determines the parameters (boldness, rotation flag, max chars per line and line count) of the given text value,
   * which are used to look up the matching text size check.
   */
  public static getTextCheckParametersFromTextValue(
    textValue: SimpleTextValueVO | CompressionSimpleTextValueVO,
  ): { fontBold: number, rotation: number, chars: number, lines: number } {
    const text: string = ConstraintCheckUtils.extractTextFromTextValue(textValue).replaceAll('\uFEFF', '');
    const lines: string[] = text.split('\n');
    return {
      fontBold: ConstraintCheckUtils.isTextValueBold(textValue) ? 1 : 0,
      rotation: textValue?.rotation === 90 || textValue?.rotation === 270 ? 1 : 0,
      chars: Math.max(0, ...lines.map((line) => line.length)),
      lines: lines.length,
    };
  }

  private static extractTextFromTextValue(textValue: SimpleTextValueVO | CompressionSimpleTextValueVO): string {
    if (!textValue?.text) {
      return '';
    }

    let text: string = textValue.text.replaceAll('\r\n', '\n');
    if (/<\/?[a-z][^>]*>/i.test(text)) {
      const span: HTMLSpanElement = document.createElement('span');
      span.innerHTML = text.replaceAll(/<\/p>/gi, '#linebreak</p>');
      text = (span.innerText ?? '').replaceAll('#linebreak', '\n');
    }

    while (text.endsWith('\n')) {
      text = text.substring(0, text.length - 1);
    }

    return text;
  }

  private static isTextValueBold(textValue: SimpleTextValueVO | CompressionSimpleTextValueVO): boolean {
    return /<(strong|b)\b/i.test(textValue?.text ?? '');
  }
}
